import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Agent } from "@/types/agent/agent";
import { useAuth } from "@/auth/AuthProvider";

interface AnalysisFiltersProps {
  agents: Agent[];
  selectedAgent: string | null;
  onSelectedAgentChange: (agentId: string | null) => void;
  analysisFilter: string;
  onAnalysisFilterChange: (filter: string) => void;
  dateRange: string;
  onDateRangeChange: (range: string) => void;
}

const analysisFilterOptions = [
  { value: "all", label: "All Conversations" },
  { value: "analyzed", label: "Analyzed Only" },
  { value: "unanalyzed", label: "Unanalyzed Only" },
];

const dateRangeOptions = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: "All time" },
];

export const AnalysisFilters = ({
  agents,
  selectedAgent,
  onSelectedAgentChange,
  analysisFilter,
  onAnalysisFilterChange,
  dateRange,
  onDateRangeChange,
}: AnalysisFiltersProps) => {
  const { user } = useAuth();

  const isTeamAgent = (agent: Agent) => agent.user_id !== user?.id;

  return (
    <Card className="p-4 sm:p-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
        <div className="flex-1 min-w-full sm:min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Genie</label>
          <Select
            value={selectedAgent || "__all_genies__"}
            onValueChange={(value) =>
              onSelectedAgentChange(value === "__all_genies__" ? null : value)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a Genie to view conversations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all_genies__">All Genies</SelectItem>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id} className="flex items-center justify-between">
                  <div className="flex items-center justify-between w-full gap-2">
                    <span>{agent.name}</span>
                    {isTeamAgent(agent) && (
                      <span className="text-xs text-gray-400 ml-auto">Team</span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 min-w-full sm:min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-2">Analysis Status</label>
          <Select value={analysisFilter} onValueChange={onAnalysisFilterChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {analysisFilterOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 min-w-full sm:min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
          <Select value={dateRange} onValueChange={onDateRangeChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {dateRangeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </Card>
  );
};
